import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {detailPageUrl, DetailPage} from '../../../helpers'

import {useDispatch, useSelector} from 'react-redux'
import {
  allCompanyLookup,
  projectLookup,
  userProjectLookup,
} from '../../../redux/slices/lookup.slice'
import {getWaitingConfirmationGetAll} from '../../../redux/slices/paymentrequest.slice'

const selectWaitingConfirmationData = (state) => ({
  waitingConfirmation: state.paymentrequest.waitingConfirmation,
  loading: state.paymentrequest.loading
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  userProjectLookupList: state.lookup.userProjectLookupList
})

function List() {
  const intl = useIntl()
  const {waitingConfirmation, loading} = useSelector(selectWaitingConfirmationData)
  const {projectLookupList, companyLookup, userProjectLookupList} = useSelector(selectLookupData)
  const dispatch = useDispatch()

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(getWaitingConfirmationGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(projectLookup())
    dispatch(userProjectLookup())
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const Columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'WAITINGCONFIRMATION.SEARCH.PROJECTNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectName',
      Cell: ({...props}) => (
        <CustomCellDouble
          item={props}
          double='name'
          to={detailPageUrl(
            DetailPage.PaymentRequest,
            props.data[props.row.index].paymentRequestId
          )}
        />
      ),
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'WAITINGCONFIRMATION.SEARCH.COMPANYTITLE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'companyTitle',
      Cell: ({...props}) => (
        <CustomCell
          item={props}
          to={detailPageUrl(
            DetailPage.PaymentRequest,
            props.data[props.row.index].paymentRequestId
          )}
        />
      ),
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'WAITINGCONFIRMATION.SEARCH.REQUESTEDUSERNAMESURNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'requestedUserNameSurname',
      Cell: ({...props}) => (
        <CustomCell
          item={props}
          to={detailPageUrl(
            DetailPage.PaymentRequest,
            props.data[props.row.index].paymentRequestId
          )}
        />
      ),
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'WAITINGCONFIRMATION.SEARCH.APPROVEUSERNAMESURNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'approveUserNameSurname',
      Cell: ({...props}) => (
        <CustomCell
          item={props}
          to={detailPageUrl(
            DetailPage.PaymentRequest,
            props.data[props.row.index].paymentRequestId
          )}
        />
      ),
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({
            id: 'WAITINGCONFIRMATION.SEARCH.PAYMENTREQUESTCONFIRMATIONLEVELNAME',
          })}
          className='min-w-125px'
        />
      ),
      accessor: 'paymentRequestConfirmationLevelName',
      Cell: ({...props}) => (
        <CustomCell
          item={props}
          to={detailPageUrl(
            DetailPage.PaymentRequest,
            props.data[props.row.index].paymentRequestId
          )}
        />
      ),
    },
  ], [intl, handleSorting])

  const buttons = useMemo(() => [], [])

  const actionButtons = useMemo(() => [
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'WAITINGCONFIRMATION.SEARCH.PROJECTNAME',
          data: projectLookupList,
          name: 'projectId',
        },
        {
          title: 'WAITINGCONFIRMATION.SEARCH.COMPANYTITLE',
          data: companyLookup,
          name: 'companyId',
        },
        {
          title: 'WAITINGCONFIRMATION.SEARCH.REQUESTEDUSERNAMESURNAME',
          data: userProjectLookupList,
          name: 'approveUserId',
        },
      ],
    },
  ], [projectLookupList, companyLookup, userProjectLookupList])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.WAITINGCONFIRMATION'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages='WAITINGCONFIRMATION'
          buttons={buttons}
        />
        <KTCardBody>
          {!waitingConfirmation && <ListLoading />}
          {waitingConfirmation && (
            <Table
              items={waitingConfirmation}
              cols={Columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
